import React, { useState, useMemo } from 'react';

/**
 * AlertsHistory - Displays past resolved alerts
 *
 * Props:
 *   - history: Array of historical alert objects
 *   - devices: Array of monitored devices
 *   - isLoading: boolean
 */
const AlertsHistory = ({ history, devices, isLoading }) => {
  const [filterDays, setFilterDays] = useState(7);

  // Helper to format duration
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  // Helper to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('fi-FI', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  // Helper to format date for grouping
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('fi-FI', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  // Helper to get device name from device_id
  const getDeviceName = (deviceId) => {
    const device = devices.find(d =>
      (d.device_id === deviceId) || (d.id === deviceId)
    );
    return device?.name || deviceId;
  };

  // Helper to format alert type for display
  const formatAlertType = (alertType) => {
    if (!alertType) return 'Unknown';
    return alertType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Filter and group history by date
  const filteredHistory = useMemo(() => {
    if (!history || history.length === 0) return {};

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - filterDays);

    const filtered = history.filter(alert => {
      const alertDate = new Date(alert.resolved || alert.detected);
      return alertDate >= cutoffDate;
    });

    // Group by date
    const grouped = {};
    filtered.forEach(alert => {
      const dateKey = formatDate(alert.resolved || alert.detected);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(alert);
    });

    return grouped;
  }, [history, filterDays]);

  const groupedDates = Object.keys(filteredHistory);
  const totalCount = Object.values(filteredHistory).flat().length;

  if (isLoading) {
    return <div className="alerts-loading">Loading history...</div>;
  }

  return (
    <div className="alerts-history">
      <div className="history-header">
        <div className="history-filter">
          <label htmlFor="filter-days">Show last:</label>
          <select
            id="filter-days"
            value={filterDays}
            onChange={(e) => setFilterDays(Number(e.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
        <span className="history-count">
          {totalCount} resolved alert{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {groupedDates.length === 0 ? (
        <div className="alerts-empty">
          <p>No alert history for the selected period.</p>
        </div>
      ) : (
        <div className="history-list">
          {groupedDates.map(dateKey => (
            <div key={dateKey} className="history-date-group">
              <h4 className="history-date-header">{dateKey}</h4>
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Type</th>
                    <th>Resolved</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory[dateKey].map((alert, index) => (
                    <tr key={index}>
                      <td>{getDeviceName(alert.device_id)}</td>
                      <td>{formatAlertType(alert.type || alert.alert_type)}</td>
                      <td>{formatTimestamp(alert.resolved)}</td>
                      <td>{formatDuration(alert.duration_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsHistory;
